<script>
import { parseSysComment } from "../../config/comments.config";

/**
 * @typedef {Object} ParsedComment
 * @property {string} id - ID da etapa
 * @property {string} name - Nome da etapa
 * @property {string} step - Valor da etapa
 * @property {Array<Object>} comentarios - Comentários da etapa
 * @property {string} comentarios.comentario - Texto do comentário
 * @property {string} comentarios.data - Data formatada
 * @property {string} comentarios.responsavel - Autor do comentário
 */

export default {
  props: {
    /**
     * @type {Array<Object>} steps - Todas as etapas
     * @property {string} id - ID da etapa
     * @property {string} nome - Nome da etapa
     * @property {boolean} completed - Determina se a etapa já passou
     * @property {boolean} current - Etapa em análise
     * @property {Array} comments - Comentários da etapa
     */
    steps: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    /**
     * @type {Array<ParsedComment>} etapas formatadas para exibição
     * @readonly
     */
    parsedParameters() {
      const toDateTime = this.$options.filters.toDateTime;
      return this.steps
        .filter(({ completed, current }) => completed || current)
        .map(({ comments, id, ...rest }, index) => ({
          ...rest,
          id,
          comentarios: comments.map(com => {
            const sysComment = parseSysComment(com.comentario);

            return {
              comentario: sysComment || com.comentario,
              data: toDateTime(com.data),
              responsavel: com.responsavel?.nome || "Desconhecido",
              isSystemComment: !!sysComment
            };
          }),
          // Valor dinâmico: posição (1-based) na lista, que já chega na
          // ordem do fluxo parametrizado (sem sufixo "½" por nome).
          step: this.stepLabel(index)
        }))
        .reverse();
    }
  },
  methods: {
    /**
     * Rótulo de posição (1-based) da etapa na timeline. A lista já chega na
     * ordem do fluxo parametrizado; os "sub-valores ½" derivados do NOME de
     * etapas de ajuste foram removidos.
     * @param {number} index
     * @returns {string}
     */
    stepLabel(index) {
      return String(index + 1);
    },
    getStepColor(completed = false) {
      return completed ? "#6bb4ba" : "#ffc440";
    }
  }
};
</script>

<template>
  <v-timeline class="timeline-wrap" align-top dense>
    <v-timeline-item
      v-for="({ id, comentarios, completed, name, step },
      i) of parsedParameters"
      :key="`step-${id}-${i}`"
      :color="getStepColor(completed)"
      fill-dot
    >
      <template v-slot:icon>
        <span class="dot-text">{{ step }}</span>
      </template>

      <span class="workflow-name" :style="{ color: getStepColor(completed) }">{{
        name
      }}</span>

      <div v-if="!comentarios.length" class="description">
        Sem comentários.
      </div>

      <template
        v-for="({ comentario, data, isSystemComment, responsavel },
        j) of comentarios"
      >
        <div
          v-if="isSystemComment"
          :key="`description-${id}-${i}-${j}`"
          class="description"
        >
          <b>{{ responsavel }}</b>
          {{ comentario }}
          às <i>{{ data }}</i>
        </div>
        <div v-else :key="`description-${id}-${i}-${j}`" class="my-3">
          <b>{{ responsavel }}</b>
          comentou às <i>{{ data }}</i>
          <div class="pl-2">
            {{ comentario }}
          </div>
        </div>
      </template>
    </v-timeline-item>
  </v-timeline>
</template>

<style lang="scss" scoped>
.description {
  height: 38px;
  padding-top: 8px;
}

.dot-text {
  color: #ffffff;
}

.switch-field {
  position: absolute;
  right: 1rem;
  top: -1rem;
  z-index: 10;
}

.timeline-wrap {
  position: relative;
}

.workflow-name {
  color: #6bb4ba;
  font-size: 1.1rem;
  font-weight: 500;
}
</style>
